<template>
  <BaseOverlay
    v-if="user && isUserAdmin && !onboardingLoading && insuranceConfig && (showCompanyDetailsModal || showChooseHubModal)"
  >
    <div class="onboarding">
      <!-- Step 1: Company Details -->
      <div
        v-if="showCompanyDetailsModal"
        class="onboarding__step-container onboarding__step-container--company"
      >
        <h1 class="h1 mb_12 color--black-purple">{{ $t('onboarding.AddName') }}</h1>
        <p class="onboarding__step-description color--black-purple mb_32">
          {{
            $t('onboarding.ToStartUsingRenova')
          }}
        </p>
        <el-input
          class="mb_20"
          v-model="companyName"
          :maxlength="40"
          :placeholder="$t('onboarding.EnterCompanyName')"
          @change="companyName = $event"
        />
        <BaseButton
          class="mb_20"
          :disabled="!companyName"
          :loading="btnLoading"
          @click="updateCompanyName"
        >
          {{ $t('onboarding.Start') }}
        </BaseButton>
      </div>

      <!-- Step 2: Choose Hub -->
      <div
        v-else-if="showChooseHubModal"
        class="onboarding__step-container onboarding__step-container--choose-hub"
      >
        <h1 class="h1 mb_12 color--black-purple">{{ $t('onboarding.ChooseWhereToStart') }}</h1>
        <p class="onboarding__step-description color--black-purple mb_32">
          {{ $t('onboarding.YouHaveAccessToBoth') }}
        </p>

        <div
          role="radiogroup"
          :aria-label="$t('onboarding.ChooseWhereToStart')"
        >
          <div
            class="onboarding__hub-option mb_12"
            :class="{ 'onboarding__hub-option--active': selectedHub === 'sales' }"
            role="radio"
            :aria-checked="selectedHub === 'sales'"
            tabindex="0"
            @click="selectedHub = 'sales'"
            @keydown.enter.prevent="selectedHub = 'sales'"
            @keydown.space.prevent="selectedHub = 'sales'"
          >
            <div class="onboarding__hub-option-radio">
              <div v-if="selectedHub === 'sales'" class="onboarding__hub-option-radio-inner"></div>
            </div>
            <span class="onboarding__hub-option-text">{{ $t('onboarding.SalesHub') }}</span>
          </div>

          <div
            class="onboarding__hub-option mb_32"
            :class="{ 'onboarding__hub-option--active': selectedHub === 'service' }"
            role="radio"
            :aria-checked="selectedHub === 'service'"
            tabindex="0"
            @click="selectedHub = 'service'"
            @keydown.enter.prevent="selectedHub = 'service'"
            @keydown.space.prevent="selectedHub = 'service'"
          >
            <div class="onboarding__hub-option-radio">
              <div v-if="selectedHub === 'service'" class="onboarding__hub-option-radio-inner"></div>
            </div>
            <span class="onboarding__hub-option-text">{{ $t('onboarding.ServiceHub') }}</span>
          </div>
        </div>

        <BaseButton
          :disabled="!selectedHub"
          :loading="btnLoading"
          @click="chooseHub"
        >
          {{ $t('onboarding.Start') }}
        </BaseButton>
      </div>
    </div>
  </BaseOverlay>
</template>

<script>
import {mapGetters} from 'vuex';
import onboardingMixin from '@/mixins/onboardingMixin';
import licenseMixin from '@/mixins/licenseMixin';
import BaseOverlay from '@/views/RenovaDashboard/components/ui-kit/BaseOverlay';
import {
  SAVE_INSURANCE_CONFIG,
  FETCH_ONBOARDING,
  INSURANCE_DETAILS_REQUEST,
} from '@/store/actions/onboarding';
import {USER_ROLES} from '@/store/modules/user';
import BaseButton from '@/views/RenovaDashboard/components/ui-kit/Buttons/BaseButton';
import {logAmplitudeEvent} from '@/utils/amplitude';

export default {
  name: 'OnboardingComponent',
  components: {
    BaseButton,
    BaseOverlay,
  },
  computed: {
    ...mapGetters(['user', 'onboardingLoading', 'insuranceConfig', 'firstHub', 'isOnboardingSimulated']),
    ...onboardingMixin.computed,
    ...licenseMixin.computed,
    isUserAdmin() {
      return this.userRole === USER_ROLES.ADMIN;
    },
    showCompanyDetailsModal() {
      if (!this.isUserAdmin) return false;
      // In simulation mode: show company name step first (before hub selection)
      if (this.isOnboardingSimulated) return !this.firstHub && !this.companyNameSubmitted;
      return !this.insuranceConfig.name;
    },
    showChooseHubModal() {
      if (!this.isUserAdmin || !this.bothHubsEnabled) return false;
      if (this.firstHub) return false;
      // In simulation: show hub selection after company name step is submitted
      if (this.isOnboardingSimulated) return this.companyNameSubmitted;
      return this.effectiveLeadCount === 0 && this.effectivePolicyCount === 0;
    },
  },
  data() {
    return {
      companyName: '',
      btnLoading: false,
      selectedHub: 'sales',
      companyNameSubmitted: false,
    };
  },
  watch: {
    showChooseHubModal: {
      handler(val) {
        if (val) {
          logAmplitudeEvent('onboarding_hub_selection_shown');
        }
      },
    },
  },
  async mounted() {
    if (!this.isUserAdmin) return;
    await this.$store.dispatch(FETCH_ONBOARDING);
    this.companyName = this.insuranceConfig?.name || '';
  },
  methods: {
    async updateCompanyName() {
      if (this.btnLoading) return;
      this.btnLoading = true;
      try {
        await this.$store.dispatch(SAVE_INSURANCE_CONFIG, { name: this.companyName });
        await this.$store.dispatch(INSURANCE_DETAILS_REQUEST);
        if (this.isOnboardingSimulated) {
          this.companyNameSubmitted = true;
          if (!this.bothHubsEnabled) {
            const hub = this.isSalesHubEnabled ? 'sales' : 'service';
            this.$store.commit('SET_FIRST_HUB', hub);
            this.$router.push(this.isSalesHubEnabled ? '/admin/leads' : '/admin/policies');
          }
          // bothHubsEnabled: hub selection modal will appear (companyNameSubmitted = true)
        } else if (this.bothHubsEnabled) {
          // Hub selection modal will appear automatically (firstHub still null)
        } else {
          const hub = this.isSalesHubEnabled ? 'sales' : 'service';
          this.$store.commit('SET_FIRST_HUB', hub);
          this.$router.push(this.isSalesHubEnabled ? '/admin/leads' : '/admin/policies');
        }
      } catch (error) {
        console.error('Error saving insurance config:', error);
      } finally {
        this.btnLoading = false;
      }
    },
    async chooseHub() {
      this.btnLoading = true;
      try {
        logAmplitudeEvent('onboarding_hub_selected', { selected_hub: this.selectedHub });
        this.$store.commit('SET_FIRST_HUB', this.selectedHub);
        this.$router.push(this.selectedHub === 'sales' ? '/admin/leads' : '/admin/policies');
      } finally {
        this.btnLoading = false;
      }
    },
  },
};
</script>

<style lang="scss">
.onboarding {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: calc(100vw - 240px);
  height: 100vh;
  background-color: #ffffff;
  border-radius: 0px;
  overflow: hidden;
  margin-left: auto;
}

.onboarding__step-container {
  display: flex;
  flex-direction: column;

  .base-button {
    width: 340px;
  }

  &--company {
    width: 340px;
    align-items: center;
    text-align: center;
  }

  &--choose-hub {
    align-items: center;

    .onboarding__step-description {
      width: 340px;
    }
  }
}

.onboarding__step-description {
  font-family: "Inter";
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0%;
  text-align: center !important;

}

h1.h1 {
  .onboarding__step-container--choose-hub & {
    text-align: center;
  }
}

.onboarding__hub-option {
  display: flex;
  position: relative;
  width: 340px;
  align-items: center;
  padding: 16px;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #5C59F5;
  }

  &--active {
    border-color: #5C59F5;
    background-color: #F9FAFB;
  }
}

.onboarding__hub-option-radio {
  position: absolute;
  left: -34px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  width: 20px;
  height: 20px;
  border: 2px solid #D1D5DB;
  border-radius: 50%;

  .onboarding__hub-option--active & {
    border-color: #5C59F5;
  }
}

.onboarding__hub-option-radio-inner {
  width: 10px;
  height: 10px;
  background-color: #5C59F5;
  border-radius: 50%;
}

.onboarding__hub-option-text {
  font-family: "Inter";
  font-weight: 500;
  font-size: 16px;
  color: #111827;
}
</style>
